import React, { useState, useEffect } from 'react';
import { Star, Users, Check, Clock, Calendar, Info, MapPin } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useBooking } from '../context/BookingContext';
import crysta from "../Assests/fleetimages/car_innova.svg";
import ertiga from "../Assests/fleetimages/car_suv.svg";
import Dezire from "../Assests/fleetimages/car_sedan.svg";
import etios from "../Assests/fleetimages/car_etios.svg";
import { getDistanceBetweenLocations, geocodeLocation } from "../services/distanceService";
import { routeChargesService } from "../services/routeChargesService";

const getOneWayIncludedKm = (distance) => {
  const numericDistance = Number(distance) || 0;
  return Math.max(130, Math.ceil(numericDistance / 25) * 25);
};

const getOneWayRouteFee = (vehicle) => {
  if (vehicle.category === 'innova') return 470;
  if (vehicle.category === 'suv') return 465;
  return 365;
};

const VehicleSelection = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { bookingData, setBookingData } = useBooking();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSeater, setSelectedSeater] = useState(null);
  const [isInterState, setIsInterState] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [routeCharges, setRouteCharges] = useState({});
  const [bookingDetails, setBookingDetails] = useState({
    from: '',
    to: '',
    fromState: '',
    toState: '',
    pickupDate: '',
    tripType: '',
    distance: null,
    duration: null
  });

  useEffect(() => {
    const locationState = location.state || {};
    const contextData = bookingData || {};
    
    const mergedData = {
      ...contextData,
      ...locationState
    };
    
    setBookingDetails({
      from: mergedData.from || '',
      to: mergedData.to || '',
      fromState: mergedData.fromState || '',
      toState: mergedData.toState || '',
      pickupDate: mergedData.pickupDate || '',
      tripType: mergedData.tripType || 'oneway',
      pickupTime: mergedData.pickupTime || '',
      distance: mergedData.distance || 137.523,
      duration: mergedData.duration || 2,
      service: mergedData.service || '',
      returnDate: mergedData.returnDate || '',
      rentalPackage: mergedData.rentalPackage || 'oneway',
      totalDays: mergedData.totalDays || 1,
    });
    
    if (Object.keys(locationState).length > 0) {
      setBookingData(locationState);
    }
  }, [location.state, bookingData, setBookingData]);

  // Fetch route charges automatically
  useEffect(() => {
    const fetchRouteCharges = async () => {
      if (bookingDetails.from && bookingDetails.to && bookingDetails.distance) {
        try {
          const charges = {};
          const vehicleTypes = Object.values(vehicleCategories).flat();
          
          for (const vehicle of vehicleTypes) {
            const result = await routeChargesService.calculateCharges(
              bookingDetails.from,
              bookingDetails.to,
              vehicle.seaters,
              parseFloat(bookingDetails.distance),
              bookingDetails.tripType
            );
            
            charges[vehicle.id] = result;
            
            if (result.isInterState) {
              setIsInterState(true);
            }
          }
          
          setRouteCharges(charges);
        } catch (error) {
          console.error('Error fetching route charges:', error);
        }
      }
    };
    
    fetchRouteCharges();
  }, [bookingDetails.from, bookingDetails.to, bookingDetails.distance, bookingDetails.tripType]);

  // Function to get state from location name
  const getStateFromLocationName = async (locationName) => {
    try {
      const result = await geocodeLocation(locationName);
      return result?.state || '';
    } catch (error) {
      console.error('Error getting state:', error);
      return '';
    }
  };

  // Update states when locations change
  useEffect(() => {
    const updateStates = async () => {
      if (bookingDetails.from && bookingDetails.to) {
        setLoadingStates(true);
        
        const fromState = await getStateFromLocationName(bookingDetails.from);
        const toState = await getStateFromLocationName(bookingDetails.to);
        
        setBookingDetails(prev => ({
          ...prev,
          fromState,
          toState
        }));
        
        setLoadingStates(false);
      }
    };
    
    updateStates();
  }, [bookingDetails.from, bookingDetails.to]);

  // Vehicle categories
  const vehicleCategories = {
    sedan: [
      {
        id: 13,
        name: 'Sedan',
        type: '4 Seater Sedan',
        category: 'sedan',
        seaters: 4,
        rating: 4.8,
        perKmRate: 15.75,
        oneWayPackageBase: -200,
        driverAllowance: 400,
        gst: 5,
        features: ['AC', 'Economical', 'Spacious Boot', 'Music System', 'Bottle Holder'],
        image: Dezire,
        luggage: '2 Suitcases',
        ac: true
      },
      {
        id: 16,
        name: 'Toyota Etios',
        type: '4 Seater Sedan',
        category: 'sedan',
        seaters: 4,
        rating: 4.7,
        perKmRate: 15.75,
        oneWayPackageBase: -88,
        driverAllowance: 400,
        gst: 5,
        features: ['AC', 'Fuel Efficient', 'Reliable', 'Ample Space', 'Digital Console'],
        image: etios,
        luggage: '2 Suitcases',
        ac: true
      }
    ],
    suv: [
      {
        id: 6,
        name: 'Prime Suv',
        type: '6 Seater MPV',
        category: 'suv',
        seaters: 6,
        rating: 5.0,
        perKmRate: 19,
        oneWayPackageBase: 1663,
        driverAllowance: 450,
        gst: 5,
        features: ['AC', 'Power Steering', 'Music System', 'Rear AC', 'Captain Seats'],
        image: ertiga,
        luggage: '4 Suitcases',
        ac: true
      }
    ],
    innova: [
      {
        id: 4,
        name: 'Toyota Innova Crysta',
        type: '7 Seater SUV',
        category: 'innova',
        seaters: 7,
        rating: 5.0,
        perKmRate: 23,
        oneWayPackageBase: 3726,
        driverAllowance: 500,
        gst: 5,
        features: ['Premium AC', 'Leather Seats', 'Bluetooth', 'Push Back Seats', 'Charging Ports'],
        image: crysta,
        luggage: '5 Suitcases',
        ac: true
      },
      {
        id: 5,
        name: 'Toyota Innova Hybrid',
        type: '7 Seater Premium',
        category: 'innova',
        seaters: 7,
        rating: 5.0,
        perKmRate: 23,
        oneWayPackageBase: 3850,
        driverAllowance: 500,
        gst: 5,
        features: ['Hybrid Engine', 'Premium AC', 'Leather Seats', 'Touchscreen', 'Ventilated Seats'],
        image: crysta,
        luggage: '5 Suitcases',
        ac: true
      }
    ],
  };

  const fetchAccurateDistance = async (from, to) => {
    try {
      const result = await getDistanceBetweenLocations(from, to);
      return result.distance;
    } catch (error) {
      console.error('Error fetching distance:', error);
      return 137.523;
    }
  };

  useEffect(() => {
    const getDistance = async () => {
      if (bookingDetails.from && bookingDetails.to) {
        const distance = await fetchAccurateDistance(
          bookingDetails.from, 
          bookingDetails.to
        );
        setBookingDetails(prev => ({
          ...prev,
          distance: parseFloat(distance).toFixed(3)
        }));
      }
    };
    
    getDistance();
  }, [bookingDetails.from, bookingDetails.to]);

  const calculateOneWayPrice = (vehicle) => {
    const distance = parseFloat(bookingDetails.distance) || 137.523;
    const includedKm = getOneWayIncludedKm(distance);
    const routeChargesForVehicle = routeCharges[vehicle.id] || {
      isInterState: isInterState
    };

    const distanceFare = Math.round(vehicle.perKmRate * includedKm);
    const packageBase = vehicle.oneWayPackageBase || 0;
    const tripFare = Math.max(0, distanceFare + packageBase);
    const driverAllowance = 0;
    const extraCharges = getOneWayRouteFee(vehicle);
    const estimatedTax = Math.round(tripFare * vehicle.gst / 100);
    const totalFare = tripFare + driverAllowance + extraCharges + estimatedTax;

    return {
      tripFare,
      driverAllowance,
      extraCharges,
      estimatedTax,
      totalFare,
      distance: distance.toFixed(3),
      includedKm,
      packageBase,
      distanceFare,
      timeFare: 0,
      platformFee: 0,
      nightCharge: 0,
      perKmRate: vehicle.perKmRate,
      isInterState: routeChargesForVehicle.isInterState,
      fareModel: 'outstation'
    };
  };

  const calculateRoundTripPrice = (vehicle) => {
    const totalDays = bookingDetails.totalDays || 2;
    const perDayKm = 250;
    const charges = routeCharges[vehicle.id] || {
      totalExtraCharges: isInterState ? 860 : 200,
      isInterState: isInterState
    };
    
    const tripFare = Math.round(totalDays * perDayKm * vehicle.perKmRate);
    const driverAllowance = totalDays * vehicle.driverAllowance;
    const extraCharges = charges.totalExtraCharges;
    const subtotalForTax = tripFare + driverAllowance + extraCharges;
    const estimatedTax = Math.round(subtotalForTax * vehicle.gst / 100);
    const totalFare = subtotalForTax + estimatedTax;
    
    return {
      tripFare,
      driverAllowance,
      extraCharges,
      estimatedTax,
      totalFare,
      perDayKm,
      totalDays,
      perKmRate: vehicle.perKmRate,
      isInterState: charges.isInterState
    };
  };

  const getPriceDetails = (vehicle) => {
    const tripType = bookingDetails.tripType || 'oneway';
    if (tripType === 'roundtrip') {
      return calculateRoundTripPrice(vehicle);
    } else {
      return calculateOneWayPrice(vehicle);
    }
  };

  const getFilteredVehicles = () => {
    let vehicles = [];
    if (selectedCategory === 'all') {
      vehicles = Object.values(vehicleCategories).flat();
    } else {
      vehicles = vehicleCategories[selectedCategory] || [];
    }
    
    if (selectedSeater) {
      return vehicles.filter(vehicle => vehicle.seaters === selectedSeater);
    }
    
    return vehicles;
  };

  const handleVehicleSelect = (vehicle) => {
    const priceDetails = getPriceDetails(vehicle);
    
    const updatedBookingData = {
      ...bookingData,
      ...bookingDetails,
      selectedVehicle: {
        id: vehicle.id,
        name: vehicle.name,
        type: vehicle.type,
        seaters: vehicle.seaters,
        features: vehicle.features,
        image: vehicle.image
      },
      vehicleDetails: vehicle,
      priceBreakdown: priceDetails,
      totalPrice: priceDetails.totalFare,
      isInterState,
      vehicleSelectedAt: new Date().toISOString()
    };
    
    setBookingData(updatedBookingData);
    
    navigate('/contact-details', { 
      state: updatedBookingData
    });
  };

  const getAvailableSeaters = () => {
    if (selectedCategory === 'all') {
      const allSeaters = Object.values(vehicleCategories)
        .flat()
        .map(v => v.seaters);
      return [...new Set(allSeaters)].sort((a, b) => a - b);
    }
    
    if (vehicleCategories[selectedCategory]) {
      const seaters = vehicleCategories[selectedCategory]
        .map(v => v.seaters);
      return [...new Set(seaters)].sort((a, b) => a - b);
    }
    
    return [];
  };

  const handleSeaterSelect = (seater) => {
    if (selectedSeater === seater) {
      setSelectedSeater(null);
    } else {
      setSelectedSeater(seater);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 -mt-12">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-3 sm:px-4 py-2 sm:py-3">
          <div className="flex justify-between items-center mb-2">
            <button
              onClick={() => navigate('/')}
              className="text-gray-500 hover:text-gray-700 text-xs sm:text-sm font-medium flex items-center gap-1"
            >
              ← Back
            </button>
            <span className="text-gray-500 text-xs">Select Vehicle</span>
          </div>
          
          {/* Route and Details */}
          <div className="flex flex-col gap-2">
            <h2 className="text-base sm:text-lg md:text-xl font-bold text-gray-900">
              {bookingDetails.from || 'Select pickup'} → {bookingDetails.to || 'Select destination'}
            </h2>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <div className="bg-gray-100 px-2 py-1 rounded flex items-center gap-1">
                <Calendar size={12} className="text-gray-500" />
                <span className="font-medium">{bookingDetails.pickupDate || 'Date'}</span>
              </div>
              <div className="bg-gray-100 px-2 py-1 rounded flex items-center gap-1">
                <Clock size={12} className="text-gray-500" />
                <span className="font-medium">{bookingDetails.pickupTime || 'Time'}</span>
              </div>
              {bookingDetails.tripType === 'roundtrip' && bookingDetails.returnDate && (
                <div className="bg-gray-100 px-2 py-1 rounded flex items-center gap-1">
                  <Calendar size={12} className="text-gray-500" />
                  <span className="font-medium">Return: {bookingDetails.returnDate}</span>
                </div>
              )}
              {bookingDetails.distance && (
                <div className="bg-gray-100 px-2 py-1 rounded flex items-center gap-1">
                  <MapPin size={12} className="text-gray-500" />
                  <span className="font-medium">{bookingDetails.distance} km</span>
                </div>
              )}
              {isInterState && (
                <div className="bg-purple-100 text-purple-700 px-2 py-1 rounded flex items-center gap-1">
                  <Info size={12} />
                  <span className="font-medium">Inter-State Travel</span>
                </div>
              )}
              {bookingDetails.tripType === 'roundtrip' && (
                <div className="bg-blue-100 text-blue-700 px-2 py-1 rounded flex items-center gap-1">
                  <Clock size={12} />
                  <span className="font-medium">{bookingDetails.totalDays || 2} Days Round Trip</span>
                </div>
              )}
              <button 
                onClick={() => navigate('/')}
                className="text-red-500 hover:text-red-600 text-xs font-medium underline ml-auto"
              >
                Edit
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Category Filters */}
      <div className="bg-white py-2 sm:py-3 border-b">
        <div className="max-w-5xl mx-auto px-3">
          <div className="overflow-x-auto pb-2 -mx-3 px-3">
            <div className="flex gap-2 min-w-max sm:min-w-0 sm:flex-wrap mb-2">
              <button
                onClick={() => {
                  setSelectedCategory('all');
                  setSelectedSeater(null);
                }}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  selectedCategory === 'all' 
                  ? 'bg-gradient-to-r from-red-600 to-orange-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All Vehicles
              </button>
              <button
                onClick={() => {
                  setSelectedCategory('sedan');
                  setSelectedSeater(null);
                }}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  selectedCategory === 'sedan' 
                  ? 'bg-gradient-to-r from-red-600 to-orange-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Sedan
              </button>
              <button
                onClick={() => {
                  setSelectedCategory('innova');
                  setSelectedSeater(null);
                }}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  selectedCategory === 'innova' 
                  ? 'bg-gradient-to-r from-red-600 to-orange-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Innova
              </button>
              <button
                onClick={() => {
                  setSelectedCategory('suv');
                  setSelectedSeater(null);
                }}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  selectedCategory === 'suv' 
                  ? 'bg-gradient-to-r from-red-600 to-orange-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                SUV/MPV
              </button>
            </div>
          </div>

          {/* Seater Filters */}
          <div className="mt-2">
            <div className="text-xs text-gray-500 mb-1.5">Filter by Seater:</div>
            <div className="overflow-x-auto pb-2 -mx-3 px-3">
              <div className="flex gap-1.5 min-w-max sm:min-w-0 sm:flex-wrap">
                {getAvailableSeaters().map((seater) => (
                  <button
                    key={seater}
                    onClick={() => handleSeaterSelect(seater)}
                    className={`px-2.5 sm:px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1 transition-all whitespace-nowrap ${
                      selectedSeater === seater
                      ? 'bg-orange-100 text-orange-600 border border-orange-300'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <Users size={12} />
                    {seater} Seater
                    {selectedSeater === seater && <Check size={12} />}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Vehicle Cards */}
      <div className="max-w-5xl mx-auto px-2 sm:px-3 py-3 sm:py-4">
        {getFilteredVehicles().length === 0 ? (
          <div className="text-center py-8">
            <div className="text-4xl mb-2">🚗</div>
            <p className="text-gray-500 text-sm">No vehicles found for the selected filters</p>
            <button
              onClick={() => {
                setSelectedSeater(null);
                setSelectedCategory('all');
              }}
              className="mt-3 text-red-600 hover:text-red-700 text-sm font-medium"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {getFilteredVehicles().map((vehicle) => {
              const priceDetails = getPriceDetails(vehicle);
              const isRoundTrip = bookingDetails.tripType === 'roundtrip';
              
              return (
                <div
                  key={vehicle.id}
                  className="bg-white rounded-lg shadow-sm hover:shadow-md border border-gray-200 overflow-hidden transition-all relative"
                >
                  {/* Trip Type Badge */}
                  {isRoundTrip && (
                    <div className="absolute top-2 right-2 bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 z-10">
                      <Clock size={10} />
                      {priceDetails.totalDays} Days Package
                    </div>
                  )}
                  
                  {/* Inter-State Badge */}
                  {priceDetails.isInterState && (
                    <div className="absolute top-2 left-2 bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 z-10">
                      <Info size={10} />
                      Permit Included
                    </div>
                  )}
                  
                  <div className="p-3 sm:p-4">
                    <div className="flex gap-3 sm:gap-4">
                      {/* Vehicle Image */}
                      <div className="w-24 sm:w-28 flex-shrink-0">
                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-2 h-24 sm:h-28 flex flex-col items-center justify-center">
                          <img 
                            src={vehicle.image || crysta}
                            alt={vehicle.name}
                            className="w-full h-full object-contain"
                          />
                        </div>
                      </div>

                      {/* Vehicle Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h3 className="text-base font-bold text-gray-900">{vehicle.name}</h3>
                            <p className="text-sm text-gray-600">{vehicle.type}</p>
                          </div>
                          <span className="bg-green-600 text-white px-1.5 py-0.5 rounded-full text-xs flex items-center gap-0.5">
                            {vehicle.rating} <Star size={10} className="fill-white" />
                          </span>
                        </div>

                        {/* Features */}
                        <div className="flex flex-wrap gap-1 mt-2">
                          {vehicle.features.slice(0, 3).map((feature, index) => (
                            <span 
                              key={index}
                              className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded"
                            >
                              {feature}
                            </span>
                          ))}
                        </div>

                        {/* Vehicle Specs without prices */}
                        <div className="flex flex-wrap items-center gap-3 sm:gap-4 mt-3 py-2.5 px-3 bg-gray-50 rounded-lg text-xs text-gray-700">
                          <span className="flex items-center gap-1.5 font-medium">
                            <Users size={14} className="text-gray-500" /> {vehicle.seaters} Seats
                          </span>
                          {vehicle.luggage && (
                            <span className="flex items-center gap-1.5 font-medium">
                              <Info size={14} className="text-gray-500" /> {vehicle.luggage}
                            </span>
                          )}
                          <span className="flex items-center gap-1.5 font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded">
                            AC Vehicle • Verified Chauffeur
                          </span>
                        </div>

                        {/* Select Button */}
                        <button
                          onClick={() => handleVehicleSelect(vehicle)}
                          className="w-full mt-3 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-semibold py-2.5 px-4 rounded-lg text-sm shadow-sm transition-all"
                        >
                          SELECT VEHICLE
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default VehicleSelection;
